import React from 'react';
import { Zap, AlertTriangle, TrendingUp, ShoppingCart } from 'lucide-react';

interface Part {
    name: string;
    part_number: string;
    availability: string;
    price: string;
    type: 'OEM' | 'AFTERMARKET';
    suitability: 'EXCELLENT' | 'DECENT' | 'AVOID';
    local_stock?: boolean;
}

interface PartsIntelligenceCardProps {
    parts: Part[];
    loading?: boolean;
    onRequestRarePart?: (part?: Part) => void;
}

const SuitabilityBadge = ({ suitability }: { suitability: Part['suitability'] }) => {
    switch (suitability) {
        case 'EXCELLENT':
            return <span className="flex items-center gap-1 text-[8px] bg-green-500/10 text-green-400 px-1.5 py-0.5 rounded border border-green-500/20 font-black uppercase">مثبت كفاءة بالسودان</span>;
        case 'DECENT':
            return <span className="flex items-center gap-1 text-[8px] bg-yellow-500/10 text-yellow-400 px-1.5 py-0.5 rounded border border-yellow-500/20 font-black uppercase">مناسب جو السودان</span>;
        case 'AVOID':
            return <span className="flex items-center gap-1 text-[8px] bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded border border-red-500/20 font-black uppercase">لا ينصح به للسودان</span>;
        default:
            return null;
    }
};

export const PartsIntelligenceCard: React.FC<PartsIntelligenceCardProps> = ({ parts, loading, onRequestRarePart }) => {
    if (loading) {
        return (
            <div className="bg-charcoal-light/50 border border-white/10 rounded-3xl p-6 animate-pulse">
                <div className="h-4 w-1/3 bg-white/10 rounded mb-4"></div>
                <div className="space-y-3">
                    <div className="h-10 bg-white/5 rounded-xl"></div>
                    <div className="h-10 bg-white/5 rounded-xl"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-charcoal-light border border-white/10 rounded-3xl overflow-hidden shadow-xl">
            <div className="bg-primary/10 border-b border-primary/20 p-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-primary">
                    <Zap size={18} />
                    <span className="font-bold text-sm uppercase">ذكاء قطع الغيار (Parts AI)</span>
                </div>
                <TrendingUp size={16} className="text-primary/50" />
            </div>

            <div className="p-4 space-y-4">
                {/* Categorized List */}
                <div className="space-y-2">
                    <h5 className="text-[9px] font-black text-gray-500 uppercase tracking-widest px-1">القطع المتوافقة تماماً</h5>
                    {parts.map((part, idx) => (
                        <div key={idx} className={`bg-white/5 border border-white/5 rounded-2xl p-4 transition-all group hover:bg-white/[0.07] ${part.type === 'OEM' ? 'border-l-4 border-l-primary' : 'border-l-4 border-l-blue-400'}`}>
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="text-white text-sm font-bold">{part.name}</h4>
                                        <span className={`text-[8px] font-black px-1.5 py-0.5 rounded uppercase ${part.type === 'OEM' ? 'bg-primary text-background-dark' : 'bg-blue-400 text-background-dark'}`}>
                                            {part.type}
                                        </span>
                                    </div>
                                    <p className="text-[10px] text-primary font-mono select-all decoration-dotted underline underline-offset-4 cursor-pointer" title="اضغط للنسخ">
                                        {part.part_number}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <span className="text-xs font-black text-white">{part.price}</span>
                                    <p className="text-[8px] text-gray-500">متوسط السعر المحرك</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                                <SuitabilityBadge suitability={part.suitability} />
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => onRequestRarePart?.(part)}
                                        className="text-[10px] text-primary font-black hover:underline flex items-center gap-1 group/btn"
                                    >
                                        اطلبه الآن <ShoppingCart size={12} className="group-hover/btn:scale-110 transition-transform" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="bg-orange-500/5 border border-orange-500/10 rounded-2xl p-4 flex gap-3">
                    <AlertTriangle className="text-orange-400 shrink-0" size={16} />
                    <p className="text-[9px] text-orange-300/80 leading-relaxed text-right">
                        يا مهندس، الترشيحات دي مبنية على تقييمات الورش السودانية والحر والتراب. اطلب (OEM) لو عايز تريح بالك.
                    </p>
                </div>

                <button
                    onClick={() => onRequestRarePart?.()}
                    className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-xs font-bold text-gray-400 transition-all flex items-center justify-center gap-2 group"
                >
                    طلبت قطعة وما لقيتها؟ <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">hail</span>
                </button>
            </div>
        </div>
    );
};
